import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translate';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminProjects.css';
import '../styles/Partners.css';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/projects/`;

const emptyForm = {
  titre: '', titre_en: '', titre_ar: '',
  description: '', description_en: '', description_ar: '',
  statut: 'Actif', statut_en: '', statut_ar: '',
  beneficiaires: '+250 familles locales', beneficiaires_en: '', beneficiaires_ar: '',
  lieu_action: "Commune rurale d'Asni", lieu_action_en: '', lieu_action_ar: '',
  objectif: 'Développement communautaire', objectif_en: '', objectif_ar: '',
  date_lancement: 'Janvier 2025', date_lancement_en: '', date_lancement_ar: ''
};

const IconTrash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconPhoto = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

export function AdminProjects() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [photoModal, setPhotoModal] = useState(null);
  const [newPhoto, setNewPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'info', onConfirm: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('ALL');
  const [activeDateFilter, setActiveDateFilter] = useState('ALL');
  const [openStatusDropdown, setOpenStatusDropdown] = useState(false);
  const [openDateDropdown, setOpenDateDropdown] = useState(false);

  const STATUS_OPTIONS = [
    { key: 'ALL', label: 'Tous les statuts' },
    { key: 'EN_COURS', label: 'En cours' },
    { key: 'TERMINE', label: 'Terminé / Complété' }
  ];

  const DATE_OPTIONS = [
    { key: 'ALL', label: 'Toutes les dates' },
    { key: 'THIS_WEEK', label: 'Cette semaine' },
    { key: 'THIS_MONTH', label: 'Ce mois' }
  ];

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.custom-dropdown-container')) {
        setOpenStatusDropdown(false);
        setOpenDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Années disponibles générées dynamiquement depuis les projets
  const availableYears = [...new Set(
    projets
      .map(p => p.date_creation ? new Date(p.date_creation).getFullYear() : null)
      .filter(Boolean)
  )].sort((a, b) => b - a);

  const filteredProjets = projets.filter(p => {
    const matchesSearch = !searchQuery ||
      (p.titre && p.titre.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const statusLower = (p.statut || '').toLowerCase();
    let matchesStatus = true;
    if (activeStatusFilter === 'EN_COURS') {
      matchesStatus = statusLower.includes('cours') || statusLower.includes('actif') || statusLower.includes('lancement') || statusLower.includes('déploiement');
    } else if (activeStatusFilter === 'TERMINE') {
      matchesStatus = statusLower.includes('terminé') || statusLower.includes('complété') || statusLower.includes('succès');
    }

    let matchesDate = true;
    if (activeDateFilter !== 'ALL' && p.date_creation) {
      const pDate = new Date(p.date_creation);
      const now = new Date();
      if (activeDateFilter === 'THIS_WEEK') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - (now.getDay() || 7) + 1);
        startOfWeek.setHours(0, 0, 0, 0);
        matchesDate = pDate >= startOfWeek;
      } else if (activeDateFilter === 'THIS_MONTH') {
        matchesDate = pDate.getFullYear() === now.getFullYear() && pDate.getMonth() === now.getMonth();
      } else {
        matchesDate = pDate.getFullYear() === parseInt(activeDateFilter);
      }
    } else if (activeDateFilter !== 'ALL' && !p.date_creation) {
      matchesDate = false;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_projects') === 'true';

  const token = () => localStorage.getItem('access_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const handleUnauthorized = (response) => {
    if (response.status === 401 || response.status === 403) {
      navigate('/admin/login');
      return true;
    }
    return false;
  };

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const notify = (type, title, message) => setModal({ show: true, type, title, message, onConfirm: null });
  const confirmModal = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  const fetchProjets = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        headers: {
          ...authHeader(),
          'Accept-Language': i18n.language || 'fr'
        }
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) setProjets(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setPendingPhotos([]);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      titre: p.titre || '', titre_en: p.titre_en || '', titre_ar: p.titre_ar || '',
      description: p.description || '', description_en: p.description_en || '', description_ar: p.description_ar || '',
      statut: p.statut || 'Actif', statut_en: p.statut_en || '', statut_ar: p.statut_ar || '',
      beneficiaires: p.beneficiaires || '', beneficiaires_en: p.beneficiaires_en || '', beneficiaires_ar: p.beneficiaires_ar || '',
      lieu_action: p.lieu_action || '', lieu_action_en: p.lieu_action_en || '', lieu_action_ar: p.lieu_action_ar || '',
      objectif: p.objectif || '', objectif_en: p.objectif_en || '', objectif_ar: p.objectif_ar || '',
      date_lancement: p.date_lancement || '', date_lancement_en: p.date_lancement_en || '', date_lancement_ar: p.date_lancement_ar || ''
    });
    setPendingPhotos([]);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setPendingPhotos([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAutoTranslate = async () => {
    if (!form.titre && !form.description) {
      notify('warning', 'Champs vides', 'Veuillez remplir au moins le titre ou la description en français.');
      return;
    }

    setSubmitting(true);
    try {
      const translations = {};
      const fields = ['titre', 'description', 'statut', 'beneficiaires', 'lieu_action', 'objectif', 'date_lancement'];

      for (const field of fields) {
        if (form[field]) {
          translations[`${field}_en`] = await translateText(form[field], 'fr', 'en');
          translations[`${field}_ar`] = await translateText(form[field], 'fr', 'ar');
        }
      }

      setForm(prev => ({ ...prev, ...translations }));
      notify('success', 'Traduction terminée', 'Tous les champs ont été traduits automatiquement.');
    } catch {
      notify('error', 'Erreur', 'La traduction a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editing ? `${API_URL}${editing.id}/` : API_URL;
      const method = editing ? 'PATCH' : 'POST';
      const fd = new FormData();
      fd.append('titre', form.titre);
      fd.append('titre_en', form.titre_en);
      fd.append('titre_ar', form.titre_ar);
      fd.append('description', form.description);
      fd.append('description_en', form.description_en);
      fd.append('description_ar', form.description_ar);
      fd.append('statut', form.statut);
      fd.append('statut_en', form.statut_en);
      fd.append('statut_ar', form.statut_ar);
      fd.append('beneficiaires', form.beneficiaires);
      fd.append('beneficiaires_en', form.beneficiaires_en);
      fd.append('beneficiaires_ar', form.beneficiaires_ar);
      fd.append('lieu_action', form.lieu_action);
      fd.append('lieu_action_en', form.lieu_action_en);
      fd.append('lieu_action_ar', form.lieu_action_ar);
      fd.append('objectif', form.objectif);
      fd.append('objectif_en', form.objectif_en);
      fd.append('objectif_ar', form.objectif_ar);
      fd.append('date_lancement', form.date_lancement);
      fd.append('date_lancement_en', form.date_lancement_en);
      fd.append('date_lancement_ar', form.date_lancement_ar);

      const res = await fetch(url, {
        method,
        headers: {
          ...authHeader(),
          'Accept-Language': i18n.language || 'fr'
        },
        body: fd
      });
      if (handleUnauthorized(res)) return;

      if (res.ok) {
        const created = await res.json();
        if (pendingPhotos.length > 0) {
          await Promise.all(pendingPhotos.map(photo => {
            const photoFd = new FormData();
            photoFd.append('image', photo);
            return fetch(`${API_URL}${created.id}/photos/`, {
              method: 'POST',
              headers: authHeader(),
              body: photoFd
            });
          }));
        }
        closeForm();
        fetchProjets();
        notify('success', editing ? 'Projet modifié' : 'Projet ajouté',
          editing ? 'Modifications enregistrées.' : `Projet créé avec ${pendingPhotos.length} image(s).`);
      } else {
        const err = await res.json();
        notify('error', 'Erreur', JSON.stringify(err));
      }
    } catch {
      notify('error', 'Erreur serveur', 'Connexion impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    confirmModal('Supprimer le projet', 'Ce projet et toutes ses photos seront supprimés. Confirmer ?', async () => {
      const res = await fetch(`${API_URL}${id}/`, { method: 'DELETE', headers: authHeader() });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        fetchProjets();
        notify('success', 'Supprimé', 'Le projet a été supprimé.');
      } else {
        notify('error', 'Erreur', 'Impossible de supprimer.');
      }
    });
  };

  const openPhotoModal = (p) => {
    setPhotoModal(p);
    setNewPhoto(null);
  };

  const closePhotoModal = () => {
    setPhotoModal(null);
    setNewPhoto(null);
    fetchProjets();
  };

  const handleFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setPendingPhotos(prev => [...prev, ...newFiles]);
    }
    e.target.value = null;
  };

  const handleUploadPhoto = async () => {
    if (!newPhoto || !photoModal) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('image', newPhoto);
      const res = await fetch(`${API_URL}${photoModal.id}/photos/`, {
        method: 'POST',
        headers: authHeader(),
        body: fd,
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const updated = await res.json();
        setPhotoModal(prev => ({ ...prev, images: [...(prev.images || []), updated] }));
        setNewPhoto(null);
      } else {
        const errText = await res.text();
        notify('error', 'Erreur', `Status ${res.status}: ${errText}`);
      }
    } catch {
      notify('error', 'Erreur serveur', 'Connexion impossible.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const res = await fetch(`${API_URL}${photoModal.id}/photos/${photoId}/`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        setPhotoModal(prev => ({ ...prev, images: prev.images.filter(p => p.id !== photoId) }));
      }
    } catch {
      console.error('Erreur suppression photo');
    }
  };

  return (
    <>
      {/* ── Modal générique ── */}
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : 'warning'}`}>
              {modal.type === 'success'
                ? <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              }
            </div>
            <h3>{modal.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              {modal.onConfirm ? (
                <>
                  <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
                  <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
                </>
              ) : (
                <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal gestion photos ── */}
      {photoModal && (
        <div className="tiwizi-modal-overlay" onClick={closePhotoModal}>
          <div className="tiwizi-modal-card" style={{ maxWidth: '680px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '6px' }}>Photos — {photoModal.titre}</h3>
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '20px' }}>
              {photoModal.images?.length || 0} photo(s)
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {(photoModal.images || []).map(photo => (
                <div key={photo.id} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #eee' }}>
                  <img src={photo.image} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                  <button onClick={() => handleDeletePhoto(photo.id)}
                    style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(231,76,60,0.85)', color: 'white', border: 'none', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              {(photoModal.images || []).length === 0 && (
                <p style={{ color: '#aaa', fontStyle: 'italic', gridColumn: '1/-1' }}>Aucune photo pour le moment.</p>
              )}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
              <p style={{ fontWeight: '700', marginBottom: '12px', fontSize: '0.9rem' }}>Ajouter une photo</p>
              <label className={`upload-zone ${newPhoto ? 'has-file' : ''}`} style={{ minHeight: '100px' }}>
                {newPhoto ? (
                  <>
                    <img src={URL.createObjectURL(newPhoto)} alt="Prévisu" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                    <span style={{ fontSize: '0.8rem' }}>{newPhoto.name}</span>
                  </>
                ) : (
                  <>
                    <IconPhoto />
                    <span>Cliquer pour choisir une image</span>
                  </>
                )}
                <input type="file" accept="image/*" onChange={e => setNewPhoto(e.target.files[0] || null)} />
              </label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <button onClick={closePhotoModal} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', fontWeight: '700', cursor: 'pointer' }}>
                  Fermer
                </button>
                <button onClick={handleUploadPhoto} disabled={!newPhoto || uploadingPhoto} className="btn-submit-partner" style={{ flex: 2 }}>
                  {uploadingPhoto ? 'Upload...' : 'Ajouter la photo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Barre de recherche & Filtres ── */}
      <div className="admin-search-filter-bar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Rechercher par titre ou description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" type="button" onClick={() => setSearchQuery('')} title="Effacer">✕</button>
          )}
        </div>
        <div className="admin-filters-wrapper" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>

          {/* ── Dropdown Statut ── */}
          <div className="custom-dropdown-container">
            <button
              type="button"
              className={`custom-dropdown-button ${openStatusDropdown ? 'is-open' : ''}`}
              onClick={() => { setOpenStatusDropdown(o => !o); setOpenDateDropdown(false); }}
            >
              {STATUS_OPTIONS.find(s => s.key === activeStatusFilter)?.label || 'Tous les statuts'}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={openStatusDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </button>
            {openStatusDropdown && (
              <div className="custom-dropdown-menu">
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s.key}
                    type="button"
                    className={`custom-dropdown-item ${activeStatusFilter === s.key ? 'selected' : ''}`}
                    onClick={() => { setActiveStatusFilter(s.key); setOpenStatusDropdown(false); }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Dropdown Date ── */}
          <div className="custom-dropdown-container">
            <button
              type="button"
              className={`custom-dropdown-button ${openDateDropdown ? 'is-open' : ''}`}
              onClick={() => { setOpenDateDropdown(o => !o); setOpenStatusDropdown(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {activeDateFilter === 'ALL' ? 'Toutes les dates' : activeDateFilter === 'THIS_WEEK' ? 'Cette semaine' : activeDateFilter === 'THIS_MONTH' ? 'Ce mois' : activeDateFilter}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={openDateDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </button>
            {openDateDropdown && (
              <div className="custom-dropdown-menu">
                {DATE_OPTIONS.map(d => (
                  <button
                    key={d.key}
                    type="button"
                    className={`custom-dropdown-item ${activeDateFilter === d.key ? 'selected' : ''}`}
                    onClick={() => { setActiveDateFilter(d.key); setOpenDateDropdown(false); }}
                  >
                    {d.label}
                  </button>
                ))}
                {availableYears.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>Par année</div>
                    {availableYears.map(year => (
                      <button
                        key={year}
                        type="button"
                        className={`custom-dropdown-item ${activeDateFilter === String(year) ? 'selected' : ''}`}
                        onClick={() => { setActiveDateFilter(String(year)); setOpenDateDropdown(false); }}
                      >
                        {year}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Indicateur de résultats ── */}
      {(searchQuery || activeStatusFilter !== 'ALL' || activeDateFilter !== 'ALL') && (
        <div className="admin-results-indicator">
          <span className="results-count">
            {filteredProjets.length} {filteredProjets.length > 1 ? 'projets trouvés' : 'projet trouvé'}
          </span>
          {activeDateFilter !== 'ALL' && (
            <span className="results-year-badge">
              {activeDateFilter === 'THIS_WEEK' ? 'Date : Cette semaine' : activeDateFilter === 'THIS_MONTH' ? 'Date : Ce mois' : `Année : ${activeDateFilter}`}
            </span>
          )}
        </div>
      )}

      {/* ── Section principale ── */}
      <section className="projets-section">
        <div className="projets-header">
          <h3>
            Gestion des Projets
            <span className="count-badge dark">{projets.length}</span>
          </h3>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div className="projets-view-toggle">
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Vue Liste"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vue Grille"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
            </div>
            {canManage && (
              <button onClick={openCreate} className="btn-add-projet">+ Ajouter un projet</button>
            )}
          </div>
        </div>

        {/* ── Formulaire ── */}
        {showForm && (
          <div className="add-partner-form-container">
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>
              {editing ? 'Modifier le projet' : 'Nouveau projet'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="proj-admin-form-grid">
                <div className="form-field">
                  <label>Titre (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input type="text" name="titre" value={form.titre} onChange={handleChange} placeholder="Ex: Construction..." required />
                </div>
                <div className="form-field">
                  <label>Titre (EN)</label>
                  <input type="text" name="titre_en" value={form.titre_en} onChange={handleChange} placeholder="Ex: Construction..." />
                </div>
                <div className="form-field">
                  <label>Titre (AR)</label>
                  <input type="text" name="titre_ar" value={form.titre_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>
                <div className="form-field">
                  <label>Description (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                  <textarea name="description" value={form.description} onChange={handleChange} placeholder="Décrivez le projet..." rows={3} required />
                </div>
                <div className="form-field">
                  <label>Description (EN)</label>
                  <textarea name="description_en" value={form.description_en} onChange={handleChange} placeholder="Description..." rows={3} />
                </div>
                <div className="form-field">
                  <label>Description (AR)</label>
                  <textarea name="description_ar" value={form.description_ar} onChange={handleChange} placeholder="..." rows={3} style={{ direction: 'rtl' }} />
                </div>
                <div className="form-field">
                  <label>Statut (FR)</label>
                  <input type="text" name="statut" value={form.statut} onChange={handleChange} placeholder="Ex: Actif" />
                </div>
                <div className="form-field">
                  <label>Statut (EN)</label>
                  <input type="text" name="statut_en" value={form.statut_en} onChange={handleChange} placeholder="Ex: Active" />
                </div>
                <div className="form-field">
                  <label>Statut (AR)</label>
                  <input type="text" name="statut_ar" value={form.statut_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>

                <div className="form-field">
                  <label>Bénéficiaires (FR)</label>
                  <input type="text" name="beneficiaires" value={form.beneficiaires} onChange={handleChange} placeholder="Ex: +250 familles" />
                </div>
                <div className="form-field">
                  <label>Bénéficiaires (EN)</label>
                  <input type="text" name="beneficiaires_en" value={form.beneficiaires_en} onChange={handleChange} placeholder="Ex: +250 families" />
                </div>
                <div className="form-field">
                  <label>Bénéficiaires (AR)</label>
                  <input type="text" name="beneficiaires_ar" value={form.beneficiaires_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>

                <div className="form-field">
                  <label>Lieu d'action (FR)</label>
                  <input type="text" name="lieu_action" value={form.lieu_action} onChange={handleChange} placeholder="Ex: Asni" />
                </div>
                <div className="form-field">
                  <label>Lieu d'action (EN)</label>
                  <input type="text" name="lieu_action_en" value={form.lieu_action_en} onChange={handleChange} placeholder="Ex: Asni" />
                </div>
                <div className="form-field">
                  <label>Lieu d'action (AR)</label>
                  <input type="text" name="lieu_action_ar" value={form.lieu_action_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>

                <div className="form-field">
                  <label>Objectif (FR)</label>
                  <input type="text" name="objectif" value={form.objectif} onChange={handleChange} placeholder="Ex: Dév..." />
                </div>
                <div className="form-field">
                  <label>Objectif (EN)</label>
                  <input type="text" name="objectif_en" value={form.objectif_en} onChange={handleChange} placeholder="Ex: Dev..." />
                </div>
                <div className="form-field">
                  <label>Objectif (AR)</label>
                  <input type="text" name="objectif_ar" value={form.objectif_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>

                <div className="form-field">
                  <label>Date de lancement (FR)</label>
                  <input type="text" name="date_lancement" value={form.date_lancement} onChange={handleChange} placeholder="Ex: Janvier 2025" />
                </div>
                <div className="form-field">
                  <label>Date de lancement (EN)</label>
                  <input type="text" name="date_lancement_en" value={form.date_lancement_en} onChange={handleChange} placeholder="..." />
                </div>
                <div className="form-field">
                  <label>Date de lancement (AR)</label>
                  <input type="text" name="date_lancement_ar" value={form.date_lancement_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                </div>
              </div>

              {/* Zone photos */}
              <div className="proj-admin-photos-section">
                <p className="proj-admin-photos-title">
                  Photos du projet
                </p>
                <label className="proj-admin-photo-upload-label">
                  <IconPhoto />
                  Cliquer pour sélectionner des photos
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                    onChange={handleFilesChange} />
                </label>
                {pendingPhotos.length > 0 && (
                  <>
                    <p className="proj-admin-photos-count"> {pendingPhotos.length} photo(s) sélectionnée(s)</p>
                    <div className="proj-admin-photos-preview">
                      {pendingPhotos.map((photo, idx) => (
                        <div key={idx} className="proj-admin-photo-thumb">
                          <img src={URL.createObjectURL(photo)} alt={`Aperçu ${idx + 1}`} />
                          <div className="proj-admin-photo-thumb-name">{photo.name}</div>
                          <button type="button" className="proj-admin-photo-remove"
                            onClick={() => setPendingPhotos(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Outils de traduction</span>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    className="btn-translate-auto"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#f5c50a',
                      color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem'
                    }}
                    disabled={submitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {submitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  Remplit automatiquement tous les champs Anglais et Arabe à partir des textes Français.
                </p>
              </div>

              <div className="proj-admin-form-actions">
                <button type="button" onClick={closeForm} className="btn-cancel-proj">Annuler</button>
                <button type="submit" className="btn-submit-partner" style={{ flex: 2 }} disabled={submitting}>
                  {submitting ? 'Enregistrement...' : editing ? 'Enregistrer les modifications' : 'Ajouter le projet'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Grille / Liste de projets ── */}
        {loading ? (
          <p className="loading-text">Chargement...</p>
        ) : projets.length === 0 ? (
          <p className="empty-table">Aucun projet pour le moment.</p>
        ) : filteredProjets.length === 0 ? (
          <div className="admin-empty-state">
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <p className="admin-empty-title">Aucun projet ne correspond à votre recherche</p>
            <p className="admin-empty-sub">Essayez un autre terme ou réinitialisez les filtres.</p>
            <button type="button" className="admin-reset-btn" onClick={() => { setSearchQuery(''); setActiveStatusFilter('ALL'); }}>
              Réinitialiser les filtres
            </button>
          </div>
        ) : viewMode === 'list' ? (
          <div className="proj-admin-list-wrapper">
            <table className="proj-admin-table">
              <thead>
                <tr>
                  <th>PROJET</th>
                  <th>STATUT</th>
                  <th>BÉNÉFICIAIRES</th>
                  <th>LIEU D'ACTION</th>
                  <th>OBJECTIF</th>
                  <th>DATE DE LANCEMENT</th>
                  {canManage && <th style={{ textAlign: 'right' }}>ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {filteredProjets.map(p => {
                  const firstPhoto = p.images?.[0]?.image || null;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="proj-admin-cell-project">
                          <div className="proj-admin-cell-thumb-wrapper">
                            {firstPhoto ? (
                              <img src={firstPhoto} alt={p.titre} className="proj-admin-cell-thumb" />
                            ) : (
                              <div className="proj-admin-cell-thumb-placeholder">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21 15 16 10 5 21" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="proj-admin-cell-meta">
                            <span className="proj-admin-cell-title">{p.titre}</span>
                            <span className="proj-admin-cell-date">Ajouté le {new Date(p.date_creation).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge-custom ${p.statut && p.statut.toLowerCase() === 'actif' ? 'active' : 'inactive'}`}>
                          {p.statut || 'Actif'}
                        </span>
                      </td>
                      <td style={{ fontWeight: '600', color: '#444' }}>{p.beneficiaires || '-'}</td>
                      <td style={{ color: '#666' }}>{p.lieu_action || '-'}</td>
                      <td style={{ color: '#666', fontSize: '0.85rem' }}>{p.objectif || '-'}</td>
                      <td style={{ color: '#888' }}>{p.date_lancement || '-'}</td>
                      {canManage && (
                        <td>
                          <div className="proj-admin-cell-actions">
                            <button className="proj-admin-action-btn-circle" onClick={() => openPhotoModal(p)} title="Gérer les photos">
                              <IconPhoto />
                              <span className="photo-count-badge-inline">{p.images?.length || 0}</span>
                            </button>
                            <button className="proj-admin-action-btn-circle edit" onClick={() => openEdit(p)} title="Modifier le projet">
                              <IconEdit />
                            </button>
                            <button className="proj-admin-action-btn-circle delete" onClick={() => handleDelete(p.id)} title="Supprimer le projet">
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="proj-admin-grid">
            {filteredProjets.map(p => {
              const firstPhoto = p.images?.[0]?.image || null;
              return (
                <div key={p.id} className="proj-admin-card">
                  <div className="proj-admin-card-img">
                    {firstPhoto
                      ? <img src={firstPhoto} alt={p.titre} />
                      : <div className="proj-admin-card-img-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    }
                    <span className="proj-admin-badge-photos">
                      <IconPhoto /> {p.images?.length || 0}
                    </span>
                  </div>
                  <div className="proj-admin-card-body">
                    <h4 className="proj-admin-card-title">{p.titre}</h4>
                    <p className="proj-admin-card-desc">{p.description}</p>
                    <span className="proj-admin-card-date">{new Date(p.date_creation).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {canManage && (
                    <div className="proj-admin-card-actions">
                      <button className="proj-admin-btn" onClick={() => openPhotoModal(p)}>
                        <IconPhoto /> Photos
                      </button>
                      <button className="proj-admin-btn edit" onClick={() => openEdit(p)}>
                        <IconEdit /> Modifier
                      </button>
                      <button className="proj-admin-btn delete" onClick={() => handleDelete(p.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

export default AdminProjects;
